import api from "./api";
import {
  ok, getMe, cycleById, isStageOpen, findRecord, myRecordSummary, recordDetail, listRecords, splitItems,
  employeeFromRecord, getLookupsCached, selfStatusOf, managerStatusOf, resultStatusOf, ratingConfigFor,
  estimatePerformance, readSections, writeSections, SELF_SECTION_MAP, MANAGER_SECTION_MAP, isoUtc, nameOf,
  pMap, recordSection, performanceFromResult, DEFAULT_SECTIONS, extensionsFromHistory, extensionFor,
  publicConfig,
} from "./core";

const extensionsOf = async (recordId) => extensionsFromHistory(await recordSection(recordId, "history", []));

const APPROVED = ["Approved", "In Progress", "Completed", "Closed"];

const SECTION_NAMES = {
  self: { summary: "Key achievements", strengths: "Strengths", improvement_areas: "Challenges", development_plan: "Areas you want to develop" },
  manager: { summary: "Overall comments", strengths: "Strengths", improvement_areas: "Areas to improve", development_plan: "Development plan" },
};

function assessmentVm(record, kind) {
  const raw = (kind === "self" ? record.self_assessment : record.manager_assessment) || {};
  const map = kind === "self" ? SELF_SECTION_MAP : MANAGER_SECTION_MAP;
  const submittedAt = kind === "self" ? record.self_submitted_at : record.manager_submitted_at;
  return {
    ...readSections(raw.sections, map),
    status: kind === "self" ? selfStatusOf(record) : managerStatusOf(record),
    submittedAt: isoUtc(submittedAt),
  };
}

export async function getMySelfAssessment(cycleId) {
  const summary = await myRecordSummary(cycleId);
  const record = await recordDetail(summary.performance_record_id);
  const cycle = await cycleById(record.cycle_id);
  const config = await ratingConfigFor(cycle);
  const { goals, kras, kpis } = splitItems(record);
  const approvedGoals = goals.filter((g) => APPROVED.includes(g.status));
  const extensions = await extensionsOf(record.performance_record_id);
  const stageOpen = isStageOpen(cycle, "selfAssessment", extensions)
    && ["TRACKING", "SELF_ASSESSMENT"].includes(record.current_stage)
    && record.goal_plan?.plan_status === "APPROVED";
  return ok({
    recordId: record.performance_record_id,
    assessment: assessmentVm(record, "self"),
    goals: approvedGoals,
    kras,
    kpis,
    stageOpen,
    extension: extensionFor(cycle, "selfAssessment", extensions),
    sections: cycle?.workflow?.assessment_sections || DEFAULT_SECTIONS,
    config: publicConfig(config),
    preview: estimatePerformance({ goals: approvedGoals, kras, kpis }, config, resultStatusOf(record)),
  });
}

function assessmentBody(form, updates, { submit, sections, map, valueKey, reason }) {
  const items = [];
  const missing = [];
  updates.forEach((u) => {
    const value = u[valueKey];
    if (value === "" || value === null || value === undefined) {
      missing.push(u.id);
      return;
    }
    const achievement = Number(value);
    if (Number.isNaN(achievement) || achievement < 0) throw new Error("Achievement and actual values must be numbers of 0 or more.");
    items.push({ item_id: Number(u.id), achievement, comment: String(u.comment ?? "").trim() });
  });
  if (submit && missing.length) throw new Error("Enter a value for every required assessment item before submitting.");
  const written = writeSections(form, map, sections);
  if (submit) {
    const required = sections && sections.length ? sections : DEFAULT_SECTIONS;
    const empty = required.filter((key) => !String(written[key] ?? "").trim());
    if (empty.length) {
      const names = empty.map((key) => SECTION_NAMES[map === SELF_SECTION_MAP ? "self" : "manager"][key] || key);
      throw new Error(`Please fill: ${names.join(", ")}. All reflection boxes are required to submit.`);
    }
  }
  return { sections: written, items, reason };
}

export async function saveMySelfAssessment(cycleId, payload, submit = false) {
  const summary = await myRecordSummary(cycleId);
  const cycle = await cycleById(summary.cycle_id);
  const updates = [
    ...(payload.goalUpdates || []).map((u) => ({ id: u.id, value: u.achievement, comment: u.employeeComments })),
    ...(payload.kraUpdates || []).map((u) => ({ id: u.id, value: u.actual, comment: u.employeeComments })),
    ...(payload.kpiUpdates || []).map((u) => ({ id: u.id, value: u.actual, comment: u.employeeComments })),
  ];
  const body = assessmentBody(payload, updates, {
    submit,
    sections: cycle?.workflow?.assessment_sections,
    map: SELF_SECTION_MAP,
    valueKey: "value",
    reason: submit ? "Self assessment submitted" : "Self assessment draft saved",
  });
  const rid = summary.performance_record_id;
  return submit ? api.post(`/records/${rid}/assessments/self/submit`, body) : api.put(`/records/${rid}/assessments/self`, body);
}

async function teamRecord(record, config, lookups) {
  const { goals, kras, kpis } = splitItems(record);
  const resultStatus = resultStatusOf(record);
  let performance = estimatePerformance({ goals, kras, kpis }, config, resultStatus);
  if (record.current_result_revision_id) {
    const results = await recordSection(record.performance_record_id, "results", []);
    if (results[0]) performance = performanceFromResult(results[0], config.bands, resultStatus);
  }
  return {
    recordId: record.performance_record_id,
    employee: employeeFromRecord(record, lookups),
    selfStatus: selfStatusOf(record),
    managerStatus: managerStatusOf(record),
    resultStatus,
    performance,
  };
}

export async function getTeamAssessments(cycleId) {
  const [me, lookups, cycle] = await Promise.all([getMe(), getLookupsCached(), cycleById(cycleId)]);
  const config = await ratingConfigFor(cycle);
  const rows = (await listRecords(cycleId)).filter((r) => r.review_manager_employee_id === me.employee_id && r.employee_id !== me.employee_id);
  const records = await pMap(rows, (r) => recordDetail(r.performance_record_id), 6);
  return ok(await pMap(records, (r) => teamRecord(r, config, lookups), 6));
}

export async function getManagerAssessment(cycleId, employeeId) {
  const summary = await findRecord(cycleId, employeeId);
  const [record, lookups] = await Promise.all([recordDetail(summary.performance_record_id), getLookupsCached()]);
  const cycle = await cycleById(record.cycle_id);
  const config = await ratingConfigFor(cycle);
  const { goals, kras, kpis } = splitItems(record);
  const self = record.self_submitted_at ? assessmentVm(record, "self") : null;
  const selfItems = Object.fromEntries(((record.self_assessment || {}).items || []).map((x) => [String(x.item_id), x]));
  const withSelf = (rows) => rows.map((r) => ({
    ...r,
    achievement: r.achievement ?? (selfItems[r.id] ? Number(selfItems[r.id].achievement) : null),
    employeeComments: selfItems[r.id]?.comment || r.employeeComments,
  }));
  const extensions = await extensionsOf(record.performance_record_id);
  const stageOpen = isStageOpen(cycle, "managerReview", extensions) && record.current_stage === "MANAGER_ASSESSMENT";
  return ok({
    recordId: record.performance_record_id,
    employee: employeeFromRecord(record, lookups),
    selfAssessment: self,
    managerAssessment: assessmentVm(record, "manager"),
    goals: withSelf(goals.filter((g) => ["Approved", "In Progress", "Completed", "Closed"].includes(g.status))),
    kras: withSelf(kras),
    kpis: withSelf(kpis),
    stageOpen,
    extension: extensionFor(cycle, "managerReview", extensions),
    sections: cycle?.workflow?.assessment_sections || DEFAULT_SECTIONS,
    resultStatus: resultStatusOf(record),
    config: publicConfig(config),
    managerName: nameOf(record.review_manager_employee_id),
  });
}

export async function saveManagerAssessment(cycleId, employeeId, payload, submit = false) {
  const summary = await findRecord(cycleId, employeeId);
  const cycle = await cycleById(summary.cycle_id);
  const updates = [
    ...(payload.goalUpdates || []).map((u) => ({ id: u.id, value: u.managerAchievement, comment: u.managerComments })),
    ...(payload.kraUpdates || []).map((u) => ({ id: u.id, value: u.actual, comment: u.managerComments })),
    ...(payload.kpiUpdates || []).map((u) => ({ id: u.id, value: u.actual, comment: u.managerComments })),
  ];
  const body = assessmentBody(payload, updates, {
    submit,
    sections: cycle?.workflow?.assessment_sections,
    map: MANAGER_SECTION_MAP,
    valueKey: "value",
    reason: submit ? "Manager assessment submitted" : "Manager assessment draft saved",
  });
  const rid = summary.performance_record_id;
  return submit ? api.post(`/records/${rid}/assessments/manager/submit`, body) : api.put(`/records/${rid}/assessments/manager`, body);
}
